<template>
  <div id="premium-modal">
    <component :is="currentModal" :info="info" :closeCallback="closeCallback" :userOperationMap="userOperationMap"
      :subscriptionState="subscriptionState" v-if="modalArguments.visible" />
  </div>
</template>

<script setup lang="tsx">
import { useI18n } from "vue-i18n";
import { ref, watch, defineProps } from "vue";
import { useRouter } from "vue-router";
import Space from "@/components/common/space/index.vue";
import Subscribing from "./subscribing.vue";
import { CommonButton, PrimaryButton } from "@/components/common";
import {
  modal_banner,
  Success,
  love_letter,
  gift_card,
} from "@/assets/images/premium";
import { useModalManager } from "@/components/common/custom-modal/instance";
import type { PropType, Ref, Component } from "vue";
import type {
  ModalArguments,
  ModalMap,
  CardItemInfo,
  ModalInfo,
} from "../../type";
import { useSubscriptionInfo } from "@/store/modules/user";
import { useTrackStore } from "@/store/modules/track";
import { grantCancelDiscount } from "@/api/premium";
import { debounce, useScopeId } from "@/utils";

const emit = defineEmits(['next', 'close'])

const router = useRouter();
const scopeId = useScopeId();
const modalManager = useModalManager();

const { t } = useI18n();
const { subscriptionState } = storeToRefs(useSubscriptionInfo());
const { collectData, track, getTrackPlanName } = useTrackStore();
const info = ref(null);
const currentModal: Ref<Component | null> = ref(null);
const props = defineProps({
  modalArguments: {
    type: Object as PropType<ModalArguments>,
    required: true,
  },
  closeCallback: {
    type: Function,
    default: () => { },
  },
  userOperationMap: {
    type: Object,
    required: true,
  },
});

// modalArguments驱动弹窗
watch(
  () => props.modalArguments,
  (newArguments: ModalArguments) => {
    const { card, type, visible, downgradeInfo, giftInfo, subscribingInfo } = newArguments;
    if (!visible) {
      clearModal();
      return;
    }
    if (type) {
      if (modalMap[type].component) {
        currentModal.value = modalMap[type].component;
        if (type === "subscribing") {
          info.value = subscribingInfo;
        }
      } else {
        let info = {};
        if (card) {
          info = { ...info, card };
        }
        if (downgradeInfo) {
          info = { ...info, downgradeInfo };
        }
        if (giftInfo) {
          info = { ...info, giftInfo };
        }
        if (info) {
          modalMap[type].handler(info);
        } else {
          modalMap[type].handler();
        }
      }
    }
  },
  { deep: true },
);

const modalMap: ModalMap = {
  subscribeSuccess: {
    name: "SubscribeSuccess",
    component: null,
    handler: () => {
      subscribeSuccessModal();
    },
  },
  upgradeSuccess: {
    name: "UpgradeSuccess",
    component: null,
    handler: () => {
      upgradeSuccessModal();
    },
  },
  upgrade: {
    name: "Upgrade",
    component: null,
    handler: () => {
      upgradeModal();
    },
  },
  downgrade: {
    name: "Downgrade",
    component: null,
    handler: (info) => {
      if (info && info.card) {
        downgradeModal(info.card);
      }
    },
  },
  recoverSuccess: {
    name: "RecoverSuccess",
    component: null,
    handler: () => {
      recoverSuccessModal();
    },
  },
  downgradeSuccess: {
    name: "DowngradeSuccess",
    component: null,
    handler: () => {
      downgradeSuccessModal();
    },
  },
  subscribing: {
    name: "Subscribing",
    component: Subscribing,
    handler: () => { },
  },
  contactUs: {
    name: "ContactUs",
    component: null,
    handler: () => {
      contactUsModal();
    },
  },
  yearToMonth: {
    name: "YearToMonth",
    component: null,
    handler: () => {
      yearToMonthTipsModal();
    },
  },
  ltdTips: {
    name: "LTDTips",
    component: null,
    handler: () => {
      ltdTipsModal();
    },
  },
};

const clearModal = () => {
  info.value = null;
  currentModal.value = null;
  modalManager.modal.open = false;
};

const subscribeSuccessModal = () => {
  modalManager.applyTemplate("info", {
    title: "",
    content: (
      <>
        <div class="relative">
          <img class="w-full h-full" src={modal_banner} />
          <div class="flex items-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <img class="mr-2" src={Success} />
            <p class="text-white text-xl whitespace-nowrap">
              {t('bvPricing.subscribed-successfully')}
            </p>
          </div>
        </div>
        <div class="absolute z-10 top-5 right-5">
          <div
            class="p-0.5 w-fit h-fit cursor-pointer rounded-sm hover:bg-closeBtnHoverColor/50"
            onClick={() => modalManager.modal.onClose()}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10.0004 8.82149L16.4316 2.39023C16.5943 2.22751 16.8582 2.22751 17.0209 2.39023L17.6101 2.97948C17.7729 3.1422 17.7729 3.40602 17.6101 3.56874L11.1789 10L17.6101 16.4313C17.7729 16.594 17.7729 16.8578 17.6101 17.0205L17.0209 17.6098C16.8582 17.7725 16.5943 17.7725 16.4316 17.6098L10.0004 11.1785L3.5691 17.6098C3.40639 17.7725 3.14257 17.7725 2.97985 17.6098L2.39059 17.0205C2.22788 16.8578 2.22788 16.594 2.39059 16.4313L8.82186 10L2.39059 3.56874C2.22788 3.40602 2.22788 3.1422 2.39059 2.97948L2.97985 2.39023C3.14257 2.22751 3.40639 2.22751 3.5691 2.39023L10.0004 8.82149Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
        <div class="px-8 py-9 pt-6 bg-white rounded-br-lg rounded-bl-lg text-sm">
          <p>{t('bvPricing.as-our-esteemed-member-you-will')} :</p>
          <ul class="my-3 list-disc pl-5">
            <li>{t('bvPricing.bvRecover.features.exclusive')}</li>
            <li>{t('bvPricing.upgradeSuccessModal.benefit2')}</li>
            <li>{t('bvPricing.upgradeSuccessModal.benefit3')}</li>
            <li>{t('bvPricing.upgradeSuccessModal.benefit4')}</li>
          </ul>
          <p>
            {t('bvPricing.upgradeSuccessModal.assistanceMessage')}
          </p>
        </div>
      </>
    ),
    footer: "",
    zIndex: 3000,
  });
  modalManager.modal.showClose = false;
  modalManager.modal.onClose = () => {
    modalManager.modal.open = false;
    props.closeCallback();
  };
};

const upgradeSuccessModal = () => {
  const handleClose = () => {
  modalManager.modal.onClose()
}

  modalManager.applyTemplate("info", {
    title: "",
    content: (
      <>
        <div class="relative">
          <img class="w-full h-full" src={modal_banner} />
          <div class="flex items-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <img class="mr-2" src={Success} />
            <p class="text-white text-xl whitespace-nowrap">
              {t('bvPricing.upgragde-successfully')}
            </p>
          </div>
        </div>

        <div class="absolute z-10 top-5 right-5">
          <div
            class="p-0.5 w-fit h-fit cursor-pointer rounded-sm hover:bg-closeBtnHoverColor/50"
            onClick={handleClose}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10.0004 8.82149L16.4316 2.39023C16.5943 2.22751 16.8582 2.22751 17.0209 2.39023L17.6101 2.97948C17.7729 3.1422 17.7729 3.40602 17.6101 3.56874L11.1789 10L17.6101 16.4313C17.7729 16.594 17.7729 16.8578 17.6101 17.0205L17.0209 17.6098C16.8582 17.7725 16.5943 17.7725 16.4316 17.6098L10.0004 11.1785L3.5691 17.6098C3.40639 17.7725 3.14257 17.7725 2.97985 17.6098L2.39059 17.0205C2.22788 16.8578 2.22788 16.594 2.39059 16.4313L8.82186 10L2.39059 3.56874C2.22788 3.40602 2.22788 3.1422 2.39059 2.97948L2.97985 2.39023C3.14257 2.22751 3.40639 2.22751 3.5691 2.39023L10.0004 8.82149Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
        <div class="px-8 py-9 pt-6 bg-white rounded-br-lg rounded-bl-lg text-sm">
          <p>{t('bvPricing.as-our-esteemed-member-you-will')} :</p>
          <ul class="my-3 list-disc pl-5">
            <li>{t('bvPricing.bvRecover.features.exclusive')}</li>
            <li>{t('bvPricing.upgradeSuccessModal.benefit2')}</li>
            <li>{t('bvPricing.upgradeSuccessModal.benefit3')}</li>
            <li>{t('bvPricing.upgradeSuccessModal.benefit4')}</li>
          </ul>
          <p>
            {t('bvPricing.upgradeSuccessModal.assistanceMessage')}
          </p>
        </div>
      </>
    ),
    footer: "",
    zIndex: 3000,
  });
  modalManager.modal.showClose = false;
  modalManager.modal.onClose = () => {
    modalManager.modal.open = false;
    props.closeCallback();
  };
};

const downgradeSuccessModal = () => {
  const handleClose = () => {
  modalManager.modal.onClose()
}
  modalManager.applyTemplate("info", {
    title: "",
    content: (
      <>
        <div class="relative">
          <img class="w-full h-full" src={modal_banner} />
          <div class="flex items-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <img class="mr-2" src={Success} />
            <p class="text-white text-xl whitespace-nowrap">
              {t('bvPricing.downgrade-successfully')}
            </p>
          </div>
        </div>
        <div class="absolute z-10 top-5 right-5">
          <div
            class="w-fit h-fit cursor-pointer rounded-sm hover:bg-closeBtnHoverColor/50"
            onClick={handleClose}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10.0004 8.82149L16.4316 2.39023C16.5943 2.22751 16.8582 2.22751 17.0209 2.39023L17.6101 2.97948C17.7729 3.1422 17.7729 3.40602 17.6101 3.56874L11.1789 10L17.6101 16.4313C17.7729 16.594 17.7729 16.8578 17.6101 17.0205L17.0209 17.6098C16.8582 17.7725 16.5943 17.7725 16.4316 17.6098L10.0004 11.1785L3.5691 17.6098C3.40639 17.7725 3.14257 17.7725 2.97985 17.6098L2.39059 17.0205C2.22788 16.8578 2.22788 16.594 2.39059 16.4313L8.82186 10L2.39059 3.56874C2.22788 3.40602 2.22788 3.1422 2.39059 2.97948L2.97985 2.39023C3.14257 2.22751 3.40639 2.22751 3.5691 2.39023L10.0004 8.82149Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
        <div class="px-8 py-9 bg-white rounded-br-lg rounded-bl-lg text-sm">
          <p>
            {t('bvPricing.downgradeSuccessModal.assistanceMessage')}
          </p>
          <div class="w-full text-center mt-9 flex justify-center">
            <PrimaryButton
              onclick={modalManager.modal.onClose}
              size="extra-small"
              color="#FF5449"
              fontColor="#FFF"
            >
              {t('bvPricing.downgradeSuccessModal.continueJourney')}
            </PrimaryButton>
          </div>
        </div>
      </>
    ),
    footer: "",
    zIndex: 3000,
  });
  modalManager.modal.showClose = false;
  modalManager.modal.onClose = () => {
    modalManager.modal.open = false;
    props.closeCallback();
  };
};

const downgradeModal = (
  card: CardItemInfo,
) => {
  const handleContinue = () => {

    collectData("boolvideo_subscribe_change", {
      click: "downgrade_tips",
      plan_name: getTrackPlanName(card.packageCode),
    });

    track("boolvideo_subscribe_change");
    emit('next', { path: 'payment', packageCode: card.packageCode, subscriptionType: 'DEGRADE_SUBSCRIPTION', state: 'view' })
    modalManager.modal.onClose();
  };

  modalManager.applyTemplate("info", {
    zIndex: 3000,
    title: (
      <div class="p-6 pb-4 w-full flex justify-between">
        <p>{t("bvPricing.downgradeModal.downgradeToCardName", {cardName: card.name.toLowerCase()})}</p>
        <div
          class="p-1 w-fit h-fit cursor-pointer rounded-sm hover:bg-closeBtnHoverColor"
          onClick={() => modalManager.modal.onClose()}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M10.0004 8.82149L16.4316 2.39023C16.5943 2.22751 16.8582 2.22751 17.0209 2.39023L17.6101 2.97948C17.7729 3.1422 17.7729 3.40602 17.6101 3.56874L11.1789 10L17.6101 16.4313C17.7729 16.594 17.7729 16.8578 17.6101 17.0205L17.0209 17.6098C16.8582 17.7725 16.5943 17.7725 16.4316 17.6098L10.0004 11.1785L3.5691 17.6098C3.40639 17.7725 3.14257 17.7725 2.97985 17.6098L2.39059 17.0205C2.22788 16.8578 2.22788 16.594 2.39059 16.4313L8.82186 10L2.39059 3.56874C2.22788 3.40602 2.22788 3.1422 2.39059 2.97948L2.97985 2.39023C3.14257 2.22751 3.40639 2.22751 3.5691 2.39023L10.0004 8.82149Z"
              fill="#646A73"
            />
          </svg>
        </div>
      </div>
    ),
    content: (
      <div class="w-full px-6">
        <p>
          {t('bvPricing.downgradeModal.downgradeWarning')}
        </p>
      </div>
    ),
    footer: (
      <Space size={12} reversed>
        <PrimaryButton
          size="extra-small"
          onclick={modalManager.modal.onClose}
          color="#FF5449"
          fontColor="#FFF"
        >
          {t('bvPricing.downgradeModal.keepPlan')}
        </PrimaryButton>
        <CommonButton size="extra-small" onclick={handleContinue}>
          {t('bvPricing.downgradeModal.continue')}
        </CommonButton>
      </Space>
    ),
  });
  modalManager.modal.showClose = false;
  modalManager.modal.onClose = () => {
    modalManager.modal.open = false;
    props.closeCallback();
  };
};

const recoverSuccessModal = () => {
  modalManager.applyTemplate("info", {
    title: "",
    content: (
      <>
        <div class="relative">
          <img class="w-full h-full" src={modal_banner} />
          <div class="flex items-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <img class="mr-2" src={Success} />
            <p class="text-white text-xl whitespace-nowrap">
              {t('bvPricing.resume-successfully')}
            </p>
          </div>
        </div>

        <div class="absolute z-10 top-5 right-5">
          <div
            class="p-0.5 w-fit h-fit cursor-pointer rounded-sm hover:bg-closeBtnHoverColor/50"
            onClick={() => modalManager.modal.onClose()}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10.0004 8.82149L16.4316 2.39023C16.5943 2.22751 16.8582 2.22751 17.0209 2.39023L17.6101 2.97948C17.7729 3.1422 17.7729 3.40602 17.6101 3.56874L11.1789 10L17.6101 16.4313C17.7729 16.594 17.7729 16.8578 17.6101 17.0205L17.0209 17.6098C16.8582 17.7725 16.5943 17.7725 16.4316 17.6098L10.0004 11.1785L3.5691 17.6098C3.40639 17.7725 3.14257 17.7725 2.97985 17.6098L2.39059 17.0205C2.22788 16.8578 2.22788 16.594 2.39059 16.4313L8.82186 10L2.39059 3.56874C2.22788 3.40602 2.22788 3.1422 2.39059 2.97948L2.97985 2.39023C3.14257 2.22751 3.40639 2.22751 3.5691 2.39023L10.0004 8.82149Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
        <div class="px-8 py-9 pt-6 bg-white rounded-br-lg rounded-bl-lg text-sm">
          <p>{t('bvPricing.as-our-esteemed-member-you-will')} :</p>
          <ul class="my-3 list-disc pl-5">
            <li>{t('bvPricing.bvRecover.features.exclusive')}</li>
            <li>{t('bvPricing.upgradeSuccessModal.benefit2')}</li>
            <li>{t('bvPricing.upgradeSuccessModal.benefit3')}</li>
            <li>{t('bvPricing.upgradeSuccessModal.benefit4')}</li>
          </ul>
          <p>
            {t('bvPricing.upgradeSuccessModal.assistanceMessage')}
          </p>
        </div>
      </>
    ),
    footer: "",
    zIndex: 3000,
  });
  modalManager.modal.showClose = false;
  modalManager.modal.onClose = () => {
    modalManager.modal.open = false;
    props.closeCallback();
  };
};

const upgradeModal = () => {
  modalManager.applyTemplate("info", {
    title: " ",
    content: (
      <div class="w-full">
        <p>
          {t('bvPricing.upgradeModal.quotaWarning')}
        </p>
        <div></div>
      </div>
    ),
    footer: "",
    zIndex: 3000,
  });
  modalManager.modal.onClose = () => {
    modalManager.modal.open = false;
    props.closeCallback();
  };
};

// 年降月提示弹窗
const yearToMonthTipsModal = () => {
  modalManager.applyTemplate("info", {
    zIndex: 3000,
    title: (
      <div class="p-6 pb-4 w-full flex justify-between relative">
        <p class="flex">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 22C14.7614 22 17.2614 20.8807 19.0711 19.0711C20.8807 17.2614 22 14.7614 22 12C22 9.2386 20.8807 6.7386 19.0711 4.92893C17.2614 3.11929 14.7614 2 12 2C9.2386 2 6.7386 3.11929 4.92893 4.92893C3.11929 6.7386 2 9.2386 2 12C2 14.7614 3.11929 17.2614 4.92893 19.0711C6.7386 20.8807 9.2386 22 12 22Z"
              fill="#FF8800"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M12 18.5C12.6904 18.5 13.25 17.9404 13.25 17.25C13.25 16.5597 12.6904 16 12 16C11.3097 16 10.75 16.5597 10.75 17.25C10.75 17.9404 11.3097 18.5 12 18.5Z"
              fill="white"
            />
            <path
              d="M12 6V14"
              stroke="white"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        <span class="pl-4">{t('bvPricing.yearToMonthModal.switchToMonthly')}</span>
        </p>
        <div
          class="p-0.5 w-fit h-fit cursor-pointer rounded-sm hover:bg-closeBtnHoverColor"
          onClick={() => modalManager.modal.onClose()}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M10.0004 8.82149L16.4316 2.39023C16.5943 2.22751 16.8582 2.22751 17.0209 2.39023L17.6101 2.97948C17.7729 3.1422 17.7729 3.40602 17.6101 3.56874L11.1789 10L17.6101 16.4313C17.7729 16.594 17.7729 16.8578 17.6101 17.0205L17.0209 17.6098C16.8582 17.7725 16.5943 17.7725 16.4316 17.6098L10.0004 11.1785L3.5691 17.6098C3.40639 17.7725 3.14257 17.7725 2.97985 17.6098L2.39059 17.0205C2.22788 16.8578 2.22788 16.594 2.39059 16.4313L8.82186 10L2.39059 3.56874C2.22788 3.40602 2.22788 3.1422 2.39059 2.97948L2.97985 2.39023C3.14257 2.22751 3.40639 2.22751 3.5691 2.39023L10.0004 8.82149Z"
              fill="#646A73"
            />
          </svg>
        </div>
      </div>
    ),
    content: (
      <div class="w-full px-6">
        <p class="pl-10">
          {t('bvPricing.yearToMonthModal.alreadySubscribed')}
          <a href="mailto:support@boolvector.com" class="text-primaryColor">
            support@boolvector.com
          </a>
        </p>
      </div>
    ),
    footer: (
      <Space size={12} reversed>
        <CommonButton size="extra-small" onclick={modalManager.modal.onClose}>
          {t('bvPricing.yearToMonthModal.gotIt')}
        </CommonButton>
      </Space>
    ),
  });
  modalManager.modal.showClose = false;
  modalManager.modal.onClose = () => {
    modalManager.modal.open = false;
    props.closeCallback();
  };
};

// LTD提示弹窗
const ltdTipsModal = () => {
  modalManager.applyTemplate("info", {
    title: (
      <div class="p-6 pb-4 w-full flex justify-between relative">
        <p class="flex">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 22C14.7614 22 17.2614 20.8807 19.0711 19.0711C20.8807 17.2614 22 14.7614 22 12C22 9.2386 20.8807 6.7386 19.0711 4.92893C17.2614 3.11929 14.7614 2 12 2C9.2386 2 6.7386 3.11929 4.92893 4.92893C3.11929 6.7386 2 9.2386 2 12C2 14.7614 3.11929 17.2614 4.92893 19.0711C6.7386 20.8807 9.2386 22 12 22Z"
              fill="#FF8800"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M12 18.5C12.6904 18.5 13.25 17.9404 13.25 17.25C13.25 16.5597 12.6904 16 12 16C11.3097 16 10.75 16.5597 10.75 17.25C10.75 17.9404 11.3097 18.5 12 18.5Z"
              fill="white"
            />
            <path
              d="M12 6V14"
              stroke="white"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="pl-4">{t('bvPricing.ltdTipsModal.updatePlan')}</span>
        </p>
        <div
          class="p-0.5 w-fit h-fit cursor-pointer rounded-sm hover:bg-closeBtnHoverColor"
          onClick={() => modalManager.modal.onClose()}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M10.0004 8.82149L16.4316 2.39023C16.5943 2.22751 16.8582 2.22751 17.0209 2.39023L17.6101 2.97948C17.7729 3.1422 17.7729 3.40602 17.6101 3.56874L11.1789 10L17.6101 16.4313C17.7729 16.594 17.7729 16.8578 17.6101 17.0205L17.0209 17.6098C16.8582 17.7725 16.5943 17.7725 16.4316 17.6098L10.0004 11.1785L3.5691 17.6098C3.40639 17.7725 3.14257 17.7725 2.97985 17.6098L2.39059 17.0205C2.22788 16.8578 2.22788 16.594 2.39059 16.4313L8.82186 10L2.39059 3.56874C2.22788 3.40602 2.22788 3.1422 2.39059 2.97948L2.97985 2.39023C3.14257 2.22751 3.40639 2.22751 3.5691 2.39023L10.0004 8.82149Z"
              fill="#646A73"
            />
          </svg>
        </div>
      </div>
    ),
    content: (
      <div class="w-full px-6">
        <p class="pl-10">
          {t('bvPricing.yearToMonthModal.alreadySubscribed')}
          <a href="mailto:support@boolvector.com" class="text-primaryColor">
            support@boolvector.com
          </a>
        </p>
      </div>
    ),
    footer: (
      <Space size={12} reversed>
        <CommonButton size="extra-small" onclick={modalManager.modal.onClose}>
          {t('bvPricing.ltdTipsModal.gotIt')}
        </CommonButton>
      </Space>
    ),
    zIndex: 3000,
  });
  modalManager.modal.showClose = false;
  modalManager.modal.onClose = () => {
    modalManager.modal.open = false;
    props.closeCallback();
  };
};

const contactUsModal = () => {
  modalManager.applyTemplate("info", {
    zIndex: 3000,
    title: (
      <div class="p-6 pb-4 w-full flex justify-between z-999">
        <p>{t('bvPricing.contactUsModal.contactUs')}</p>
        <div
          class="p-0.5 w-fit h-fit cursor-pointer rounded-sm hover:bg-closeBtnHoverColor"
          onClick={() => modalManager.modal.onClose()}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M10.0004 8.82149L16.4316 2.39023C16.5943 2.22751 16.8582 2.22751 17.0209 2.39023L17.6101 2.97948C17.7729 3.1422 17.7729 3.40602 17.6101 3.56874L11.1789 10L17.6101 16.4313C17.7729 16.594 17.7729 16.8578 17.6101 17.0205L17.0209 17.6098C16.8582 17.7725 16.5943 17.7725 16.4316 17.6098L10.0004 11.1785L3.5691 17.6098C3.40639 17.7725 3.14257 17.7725 2.97985 17.6098L2.39059 17.0205C2.22788 16.8578 2.22788 16.594 2.39059 16.4313L8.82186 10L2.39059 3.56874C2.22788 3.40602 2.22788 3.1422 2.39059 2.97948L2.97985 2.39023C3.14257 2.22751 3.40639 2.22751 3.5691 2.39023L10.0004 8.82149Z"
              fill="#646A73"
            />
          </svg>
        </div>
      </div>
    ),
    content: (
      <div class="w-full px-6">
        <p>
          {t('bvPricing.contactUsModal.ourEmail')}{" "}
          <a href="mailto:support@boolvector.com" class="text-primaryColor">
            support@boolvector.com
          </a>
        </p>
        <p>
          {t('bvPricing.contactUsModal.contactMessage')}
        </p>
      </div>
    ),
    footer: (
      <Space reversed>
        <CommonButton size="extra-small" onclick={modalManager.modal.onClose}>
          {t('bvPricing.contactUsModal.gotIt')}
        </CommonButton>
      </Space>
    ),
  });
  modalManager.modal.showClose = false;
  modalManager.modal.onClose = () => {
    modalManager.modal.open = false;
    props.closeCallback();
  };
};
</script>

<style lang="scss" scoped>
.modal-container {
  padding: 0;
  position: relative;
  z-index: 10;
  border-top-right-radius: 8px;
  border-top-left-radius: 8px;
  width: 36%;
  max-width: 530px;
  min-width: 430px;
  border-radius: 8px;
}

.modal-header {
  width: 100%;

  p:first-child {
    width: 100%;
  }
}

.modal-footer {
  padding: 24px;
  margin-top: 0;
}

.modal-content {
  position: relative;
}

.modal-content {
  margin-top: 0;
}

.giftcard-button {
  :deep(.el-button) {
    width: 170px;
  }
}

.gift-card-content {
  position: absolute;
  left: 22px;
  bottom: 22px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.contact-modal-index {
  z-index: 3000;
}
</style>
